import * as React from 'react';
import { styled } from '@glitz/react';
import { SlideHeightAnimation, createFlipYDecorator } from '@avensia/excite-ui';
import { ChevronDownIcon } from 'svenssons/shared/icons';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { getComponentState, setComponentState } from '@avensia/excite';
import { isNordicNest, isSvenssons, Theme } from 'webapp/theme';
import { Button } from 'webapp/components/button';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { NnTestIdProps } from 'webapp/shared/test-ids';

export const ExpandableBox = React.forwardRef(function (
  {
    header,
    children,
    isOpen = false,
    onToggle,
    className,
    contentPadding = true,
    id,
    largeHeading = false,
    facetHierarchy = false,
    compactFacetHierarchy = false,
  }: {
    header: React.ReactNode;
    children: React.ReactNode;
    isOpen?: boolean;
    onToggle?: (isOpen: boolean) => void;
    className?: string;
    contentPadding?: boolean;
    id: string;
    largeHeading?: boolean;
    facetHierarchy?: boolean;
    compactFacetHierarchy?: boolean;
  },
  ref: React.Ref<HTMLDivElement>,
) {
  const [open, setIsOpen] = React.useState(compactFacetHierarchy ? false : getComponentState<boolean>(id, isOpen));

  React.useEffect(() => {
    setIsOpen(isOpen);
  }, [id, isOpen]);

  React.useEffect(() => {
    if (!facetHierarchy) {
      setComponentState({ id: id, state: open });
    }
  }, [id, open]);

  return (
    <div className={className}>
      <Header
        css={
          facetHierarchy &&
          styled(
            compactFacetHierarchy ? compactFacetHierarchyDecorator : facetHierarchyDecorator,
            !isOpen &&
              !compactFacetHierarchy && { borderBottom: { width: 'thin', style: 'solid', color: t => t.borderColor } },
          )
        }
        as={Theme.Transparent}
        disableRippleEffect
        onClick={() => {
          setIsOpen(!open);
          onToggle?.(!open);
        }}
        {...NnTestIdProps.FacetHeaderExpandableButton}
      >
        <HeaderText css={styled(largeHeading && largeDecorator, facetHierarchy && facetHierarchyHeaderTextDecorator)}>
          {header}
        </HeaderText>
        <ArrowUp css={styled(createFlipYDecorator(open))} />
      </Header>
      <HeightAnimation keepElement ref={ref} visible={open} css={contentPadding && paddingDecorator}>
        {children}
      </HeightAnimation>
    </div>
  );
});

const facetHierarchyDecorator = styled(
  createButtonHoverDecorator({ border: { top: { width: 0 }, bottom: { color: t => t.borderColor } } }),
  createMinLargeViewportDecorator({
    padding: { left: t => t.sharedSpacing.l, right: t => t.sharedSpacing.s, y: t => t.sharedSpacing.m },
  }),
  {
    borderTop: { width: 0 },
    height: 'auto',
  },
);

const compactFacetHierarchyDecorator = styled(
  createButtonHoverDecorator({ borderTop: { width: 0 } }),
  {
    borderTop: { width: 0 },
    height: 'auto',
  },
  createMinLargeViewportDecorator({
    padding: {
      left: t => t.sharedSpacing.m,
      right: t => t.sharedSpacing.s,
      y: t => t.sharedSpacing.xs,
    },
  }),
);

const facetHierarchyHeaderTextDecorator = styled(
  createMinLargeViewportDecorator({
    fontSize: t => t.facets?.facet?.fontSize,
  }),
  {
    height: 'auto',
  },
);

const HeightAnimation = styled(
  SlideHeightAnimation,
  {
    fontSize: t => t.sharedFontSize.xs,
  },
  isSvenssons &&
    createMinLargeViewportDecorator({
      fontSize: t => t.sharedFontSize.s,
    }),
);

const paddingDecorator = styled(
  createMaxSmallViewportDecorator({
    padding: { bottom: t => t.sharedSpacing.l },
  }),
  {
    padding: { x: t => (isNordicNest ? t.sharedSpacing.l : t.sharedSpacing.m), bottom: t => t.sharedSpacing.xl },
  },
  isNordicNest &&
    createMinLargeViewportDecorator({
      padding: { x: 0 },
    }),
);

const Header = styled(
  Button,
  createButtonHoverDecorator({
    borderTop: {
      width: 'thin',
      style: 'solid',
      color: t => t.borderColor,
    },
  }),
  {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 72,
    cursor: 'pointer',
    userSelect: 'none',
    padding: { x: t => (isNordicNest ? t.sharedSpacing.l : t.sharedSpacing.m) },
    borderTop: {
      width: 'thin',
      style: 'solid',
      color: t => t.borderColor,
    },
  },
  isNordicNest &&
    createMinLargeViewportDecorator({
      padding: { x: 0 },
      fontSize: t => t.sharedFontSize.m,
    }),
);

const HeaderText = styled.h2(
  {
    fontSize: 'inherit',
    marginBottom: 0,
    fontFamily: t => t.fontFamily?.primary,
    height: 72,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  isSvenssons
    ? {
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }
    : {
        fontWeight: t => t.fontWeight?.bold,
      },
);

const IconStyle = styled({
  fontSize: t => t.fontSize.s,
});

const ArrowUp = styled(ChevronDownIcon, IconStyle);

const largeDecorator = styled({
  fontSize: t => t.fontSize.xl,
});
